import { useMemo } from 'react';

import { useSearchParams } from 'next/navigation';
import {
	APPLICANT_MODE_WORKSPACE_ID,
	Workspace,
} from '@/features/user-settings/types/workspace-types';

import { QUERY_PARAMS } from '../constants/query-params';
import { tabStorage } from '../utils/tab-storage';
import { stripQueryParams } from '../utils/window';

interface Props {
	allWorkspaces: Workspace[];
	refetching?: boolean;
}

function useSelectWorkspace({ allWorkspaces, refetching = false }: Props) {
	const searchParams = useSearchParams();

	const routerWorkspaceId = useMemo(
		() => searchParams.get(QUERY_PARAMS.PAGE_ID) as string,
		[searchParams]
	);

	const netWorkspaceId = useMemo(() => {
		return getSelectedWorkspaceId({ allWorkspaces, routerWorkspaceId });
	}, [allWorkspaces?.length, routerWorkspaceId, refetching]);

	return {
		workspaceId: netWorkspaceId,
	};
}

export default useSelectWorkspace;

export function getSelectedWorkspaceId({
	allWorkspaces,
	routerWorkspaceId,
}: {
	allWorkspaces: Workspace[];
	routerWorkspaceId: string;
}) {
	if (typeof window === 'undefined') return null;

	const defaultWorkspaceId = allWorkspaces?.find((item) => item?.isDefaultPage)?.id || '';

	const sessionWid = tabStorage.get('selectedWorkspaceId');
	let overrideWorkspaceId = routerWorkspaceId || sessionWid || defaultWorkspaceId;

	if (overrideWorkspaceId === APPLICANT_MODE_WORKSPACE_ID) {
		tabStorage.set('selectedWorkspaceId', overrideWorkspaceId);
		return overrideWorkspaceId;
	}

	if (allWorkspaces?.length === 0) return null;

	let workspaceExists = allWorkspaces.find((workspace) => workspace.id === overrideWorkspaceId);
	if (!workspaceExists) {
		overrideWorkspaceId = defaultWorkspaceId || allWorkspaces[0]?.id;
		workspaceExists = allWorkspaces.find((workspace) => workspace.id === overrideWorkspaceId);
	}

	if (workspaceExists && overrideWorkspaceId) {
		tabStorage.set('selectedWorkspaceId', overrideWorkspaceId);

		// remove page id from url once set in session storage
		if (routerWorkspaceId) stripQueryParams(QUERY_PARAMS.PAGE_ID);

		return overrideWorkspaceId;
	}

	return null;
}
